/**
 * Botões da barra ligados à transcrição: marcador de momento e abrir/fechar o painel. Cada um só existe quando a
 * chamada usa a Jev (`TranscriptProvider` na sala). A legenda ao vivo não tem botão: é de uso eventual e fica no menu
 * "Mais opções" (`MoreMenu`).
 */
import { Bookmark, PanelBottom, PanelRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { isPanelOpen } from '../transcript/panelState';
import { useCallTranscript } from '../transcript/transcriptContext';
import { FOCUS_RING, ROUND_BUTTON, SIZE } from './controlStyles';

const CYAN = 'bg-cyan-500/15 text-cyan-300 ring-1 ring-cyan-400/45 hover:bg-cyan-500/25';

/** Marca um momento agora. @param {{ variant: keyof typeof SIZE }} props */
export function MomentButton({ variant }) {
    const transcript = useCallTranscript();
    if (!transcript) return null;
    return (
        <button
            type="button"
            aria-label="Marcar momento"
            title="Marcar momento"
            disabled={transcript.momentBusy}
            onClick={() => transcript.addMoment()}
            className={cn(ROUND_BUTTON, SIZE[variant].btn, 'disabled:opacity-60')}
        >
            {/* Só o desenho é amarelo: o botão é neutro como os outros, sem anel nem fundo chamando a atenção. */}
            <Bookmark className={cn(SIZE[variant].icon, 'text-amber-300')} aria-hidden="true" />
        </button>
    );
}

/**
 * Mostra e esconde a transcrição (o painel da chamada). Existe em todas as janelas da sala: na ampliada o painel fica ao
 * lado do vídeo; na janelinha da guia, na janela flutuante e na tela estreita ele abre como uma gaveta embaixo do vídeo.
 * @param {{ variant: keyof typeof SIZE, panelVariant?: 'room'|'compact' }} props
 *   `variant`: o tamanho dos botões da barra; `panelVariant`: a janela de que o painel é (a ampliada começa com o painel
 *   aberto, as compactas fechado). Na tela estreita os dois diferem: a barra é compacta e a janela é a ampliada.
 */
export function PanelToggleButton({ variant, panelVariant = variant }) {
    const transcript = useCallTranscript();
    if (!transcript) return null;
    const open = isPanelOpen(transcript.panelPref, panelVariant);
    const label = open ? 'Fechar a transcrição' : 'Ver a transcrição';
    // O desenho acompanha o lugar do painel: ao lado (sala ampliada larga) ou embaixo (gaveta).
    const Icon = variant === 'room' && panelVariant === 'room' ? PanelRight : PanelBottom;
    return (
        <button
            type="button"
            aria-label={label}
            title={label}
            aria-pressed={open}
            onClick={() => transcript.setPanelPref(!open)}
            className={cn('inline-flex items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20', SIZE[variant].btn, FOCUS_RING, open && CYAN)}
        >
            <Icon className={SIZE[variant].icon} aria-hidden="true" />
        </button>
    );
}
